import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { getLeaderboard, LeaderboardEntry } from "../services/quizzes";

interface LeaderboardProps {
  ageGroup?: string;
  limit?: number;
  // "dark" matches the athlete app (glass-900 surface). "light" matches admin
  // dashboards where the parent card is already white.
  variant?: "dark" | "light";
  // When true, skip the outer wrapper + header (the parent renders its own
  // title and card). Avoids the "Clasament / Clasament" duplication on
  // /dashboard/admin-club/leaderboard.
  embed?: boolean;
}

export default function Leaderboard({ ageGroup, limit = 10, variant = "dark", embed = false }: LeaderboardProps) {
  const { t } = useTranslation();
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadLeaderboard();
  }, [ageGroup, limit]);

  const loadLeaderboard = async () => {
    try {
      setLoading(true);
      const data = await getLeaderboard({ age_group: ageGroup, limit });
      setLeaderboard(data);
    } catch (error) {
      console.error("Failed to load leaderboard:", error);
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (index: number) => {
    if (index === 0) return "🥇";
    if (index === 1) return "🥈";
    if (index === 2) return "🥉";
    return `${index + 1}.`;
  };

  const isLight = variant === "light";

  const rankColor = (index: number) => {
    if (isLight) {
      if (index === 0) return "text-amber-500";
      if (index === 1) return "text-slate-400";
      if (index === 2) return "text-orange-500";
      return "text-slate-500";
    }
    if (index === 0) return "text-yellow-400";
    if (index === 1) return "text-gray-300";
    if (index === 2) return "text-orange-400";
    return "text-ink-300";
  };

  const surface = isLight
    ? "bg-white"
    : "bg-glass-900 border border-glass-700 rounded-lg p-4 sm:p-6";

  const rowBase = isLight
    ? "bg-slate-50 border border-slate-200 hover:border-indigo-300"
    : "bg-glass-800 border border-glass-700 hover:border-glass-600";
  const rowTop = isLight
    ? "bg-gradient-to-r from-amber-50 to-white border border-amber-200 hover:border-amber-300"
    : "bg-gradient-to-r from-glass-800 to-glass-900 border border-glass-600 hover:border-brand-500";

  const nameClass = isLight ? "text-slate-800" : "text-ink-100";
  const badgeClass = isLight
    ? "bg-white border border-slate-200 text-slate-600"
    : "bg-glass-700 text-ink-400";
  const subtleText = isLight ? "text-slate-500" : "text-ink-400";
  const scoreClass = isLight ? "text-indigo-600" : "text-brand-400";

  if (loading) {
    return (
      <div className={surface}>
        <div className={`text-center ${subtleText}`}>{t("common.loading", "Loading...")}</div>
      </div>
    );
  }

  if (leaderboard.length === 0) {
    return (
      <div className={surface}>
        {!embed && (
          <h3 className={`text-lg font-bold mb-4 flex items-center gap-2 ${isLight ? "text-slate-800" : "text-brand-400"}`}>
            <span>🏆</span>
            {t("leaderboard.title", "Clasament")}
            {ageGroup && <span className={`text-sm ${subtleText}`}>({ageGroup})</span>}
          </h3>
        )}
        <div className={`text-center py-8 ${subtleText}`}>
          {t("leaderboard.no_data", "Niciun rezultat încă. Fii primul care completează un quiz!")}
        </div>
      </div>
    );
  }

  const content = (
    <>
      {!embed && (
        <div className="flex items-center justify-between mb-4 sm:mb-6 gap-2 flex-wrap">
          <h3 className={`text-base sm:text-lg font-bold flex items-center gap-2 ${isLight ? "text-slate-800" : "text-brand-400"}`}>
            <span>🏆</span>
            {t("leaderboard.title", "Clasament")}
            {ageGroup && <span className={`text-xs sm:text-sm ${subtleText}`}>({ageGroup})</span>}
          </h3>
          <span className={`text-xs ${subtleText}`}>
            {t("leaderboard.top_n", "Top {{count}}", { count: limit })}
          </span>
        </div>
      )}

      <div className="space-y-2">
        {leaderboard.map((entry, index) => (
          <div
            key={entry.user_id}
            className={`p-3 sm:p-4 rounded-lg transition-all duration-200 ${
              index < 3 ? rowTop : rowBase
            }`}
          >
            {/* Mobile: 2-row layout (rank + name + score on top, badges below).
                Desktop (sm:+): single horizontal row matching the original. */}
            <div className="flex items-center gap-3 sm:gap-4">
              <div className={`text-xl sm:text-2xl font-bold ${rankColor(index)} min-w-[2rem] sm:min-w-[3rem] text-center flex-shrink-0`}>
                {getRankIcon(index)}
              </div>
              <div className="flex-1 min-w-0">
                <div className={`font-bold text-sm sm:text-base truncate ${nameClass}`}>{entry.name}</div>
                <div className={`hidden sm:flex flex-wrap gap-2 text-xs mt-1 ${subtleText}`}>
                  {entry.team && <span className={`px-2 py-0.5 rounded ${badgeClass}`}>{entry.team}</span>}
                  {entry.age_group && <span className={`px-2 py-0.5 rounded ${badgeClass}`}>{entry.age_group}</span>}
                  {entry.club && <span className={`px-2 py-0.5 rounded ${badgeClass}`}>{entry.club}</span>}
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className={`text-lg sm:text-2xl font-bold ${scoreClass}`}>{entry.total_score}</div>
                <div className={`text-[10px] sm:text-xs ${subtleText}`}>
                  {entry.quiz_count} {t("leaderboard.quizzes", "quiz-uri", { count: entry.quiz_count })}
                </div>
              </div>
            </div>
            {/* Badges row — only on mobile (desktop has them inline above). */}
            {(entry.team || entry.age_group || entry.club) && (
              <div className={`sm:hidden flex flex-wrap gap-1.5 mt-2 pl-[calc(2rem+0.75rem)] text-[10px] ${subtleText}`}>
                {entry.team && <span className={`px-1.5 py-0.5 rounded ${badgeClass}`}>{entry.team}</span>}
                {entry.age_group && <span className={`px-1.5 py-0.5 rounded ${badgeClass}`}>{entry.age_group}</span>}
                {entry.club && <span className={`px-1.5 py-0.5 rounded ${badgeClass}`}>{entry.club}</span>}
              </div>
            )}
          </div>
        ))}
      </div>

      {!embed && (
        <div className={`mt-4 text-center text-xs ${subtleText}`}>
          {t("leaderboard.explanation", "Pe baza punctelor totale acumulate din toate quiz-urile")}
        </div>
      )}
    </>
  );

  if (embed) {
    return <>{content}</>;
  }

  return <div className={surface}>{content}</div>;
}
